import { InputHTMLAttributes, useState } from 'react';
import { RegisterOptions } from 'react-hook-form';

import { RegisterType } from 'types';

import { Label } from 'components/Label';

import * as S from './Input.styles';
import { StyledInputProps } from './Input.styles';

type InputProps = InputHTMLAttributes<HTMLInputElement> & {
  name: string;
  label?: string;
  register?: RegisterType;
  options?: RegisterOptions;
  className?: string;
  onInputChange?: (arg: string) => void;
  defaultValue?: string | number;
  hasShowIcon?: boolean;
  hasClearIcon?: boolean;
  onClear?: () => void;
  isCustomerCode?: boolean;
  setValue?: (name: string, value: string) => void;
} & StyledInputProps;

const defaultProps: Partial<InputProps> = {
  name: undefined,
  options: {},
  error: undefined,
  scale: 'xs',
  label: '',
  className: '',
  motive: 'm1',
  onInputChange: undefined,
  register: undefined,
  defaultValue: undefined,
  hasShowIcon: false,
  hasClearIcon: false,
  onClear: undefined,
  isCustomerCode: false,
};

export function Input(props: InputProps): React.ReactElement {
  const {
    name,
    label,
    className,
    register,
    options,
    error,
    scale,
    motive,
    required,
    type,
    onInputChange,
    onChange,
    value,
    defaultValue,
    hasShowIcon,
    hasClearIcon,
    onClear,
    placeholder,
    disabled,
    isCustomerCode,
    setValue,
    ...rest
  } = {
    ...defaultProps,
    ...props,
  };

  const [isShowPassword, isShowPasswordSet] = useState<boolean>();
  const [hasValue, hasValueSet] = useState<boolean>(Boolean(defaultValue ?? value ?? ''));

  const handleClear = () => {
    if (setValue) setValue(name, '');
    hasValueSet(false);
    if (onClear) onClear();
  };

  const trackValue = (val: string) => {
    hasValueSet(Boolean(val));
  };

  const pickInput = () => {
    if (register && setValue && isCustomerCode) {
      return (
        <S.Input
          data-testid="input"
          type={isShowPassword ? 'text' : type || 'text'}
          error={error}
          defaultValue={defaultValue}
          {...register(name, options)}
          id={name}
          placeholder={placeholder}
          disabled={disabled}
          {...rest}
          onChange={(e) => {
            const val = e.target.value;
            setValue(name, val);
            trackValue(val);
          }}
        />
      );
    }
    if (register && onInputChange) {
      return (
        <S.Input
          data-testid="input"
          type={isShowPassword ? 'text' : type || 'text'}
          error={error}
          defaultValue={defaultValue}
          {...register(name, options)}
          id={name}
          placeholder={placeholder}
          disabled={disabled}
          {...rest}
          onChange={(e) => {
            onInputChange(e.target.value);
            trackValue(e.target.value);
          }}
        />
      );
    }

    if (register) {
      const registered = register(name, options);
      return (
        <S.Input
          data-testid="input"
          type={isShowPassword ? 'text' : type || 'text'}
          error={error}
          defaultValue={defaultValue}
          {...registered}
          placeholder={placeholder}
          id={name}
          disabled={disabled}
          {...rest}
          onChange={(e) => {
            registered.onChange(e);
            trackValue(e.target.value);
          }}
        />
      );
    }

    return (
      <S.Input
        data-testid="input"
        name={name}
        value={value}
        type={isShowPassword ? 'text' : type || 'text'}
        defaultValue={defaultValue}
        error={error}
        id={name}
        placeholder={placeholder}
        disabled={disabled}
        {...rest}
        onChange={(e) => {
          if (onChange) onChange(e);
          trackValue(e.target.value);
        }}
      />
    );
  };

  return (
    <S.Wrapper scale={scale} className={className} motive={motive}>
      {label && (
        <Label htmlFor={name} required={required} error={error}>
          {label}
        </Label>
      )}

      <S.Container>
        {pickInput()}

        {hasClearIcon && hasValue && !disabled ? (
          <S.ClearButton
            type="button"
            aria-label="Clear"
            iconLeft="FiX"
            onClick={handleClear}
          />
        ) : null}

        {hasShowIcon ? (
          <S.PasswordButton
            type="button"
            iconLeft={isShowPassword ? 'RiEyeLine' : 'RiEyeCloseLine'}
            onClick={() => isShowPasswordSet(!isShowPassword)}
          />
        ) : null}
      </S.Container>
    </S.Wrapper>
  );
}
